import { t } from "@lingui/core/macro";
import { toCents } from "@/utils";

export type CompanyShareType = "percentage" | "fixed";

export type PeriodPaymentState = "paid" | "partially_paid" | "not_paid";

/** `paidAt` on the period is only set once every payer has settled. */
export function getPeriodPaymentState(period: {
  paidAt: Date | string | null;
  shares: { paidAt: Date | string | null }[];
}): PeriodPaymentState {
  if (period.paidAt) return "paid";
  return period.shares.some((share) => share.paidAt)
    ? "partially_paid"
    : "not_paid";
}

/** Mirrors `calculateShares`, which drops the member's share when no remainder is left for them. */
function companyShareCoversWholePrice({
  shareType,
  shareValue,
  price,
}: {
  shareType: CompanyShareType;
  shareValue: number;
  price: number;
}): boolean {
  if (price <= 0) return false;
  return shareType === "percentage" ? shareValue >= 100 : shareValue >= price;
}

/** Fixed shares are stored in cents, percentage shares as whole percent. */
export function toStoredCompanyShareValue(
  shareType: CompanyShareType,
  shareValue: number,
): number {
  return shareType === "fixed" ? toCents(shareValue) : Math.round(shareValue);
}

export type CompanySplitErrors = {
  payerCompanyId?: string;
  companyShareValue?: string;
};

/** Empty when the split can be saved or is switched off; a fixed `shareValue` and `price` are in major units. */
export function getCompanySplitErrors({
  isEnabled,
  payerCompanyId,
  shareType,
  shareValue,
  price,
}: {
  isEnabled: boolean;
  payerCompanyId: number | null;
  shareType: CompanyShareType;
  shareValue: number;
  price: number;
}): CompanySplitErrors {
  if (!isEnabled) return {};

  const errors: CompanySplitErrors = {};
  if (payerCompanyId == null) {
    errors.payerCompanyId = t`Izberite podjetje, ki sofinancira paket.`;
  }

  if (shareType === "percentage") {
    if (!Number.isInteger(shareValue) || shareValue < 1 || shareValue > 100) {
      errors.companyShareValue = t`Vnesite celo število med 1 in 100 %.`;
    }
  } else if (!(shareValue > 0)) {
    errors.companyShareValue = t`Vnesite znesek, ki ga plačuje podjetje.`;
  } else if (price > 0 && shareValue > price) {
    errors.companyShareValue = t`Znesek presega ceno paketa. Če podjetje krije celoten paket, izberite 100 %.`;
  }

  return errors;
}

/** The co-funding fields of the subscription create and settings forms. */
export type CompanySplitFormValues = {
  companySplitEnabled: boolean;
  payerCompanyId: number | null;
  companyShareType: CompanyShareType;
  companyShareValue: number;
  price: number;
};

export function getCompanySplitFormErrors(
  values: CompanySplitFormValues,
): CompanySplitErrors {
  return getCompanySplitErrors({
    isEnabled: values.companySplitEnabled,
    payerCompanyId: values.payerCompanyId,
    shareType: values.companyShareType,
    shareValue: values.companyShareValue,
    price: values.price,
  });
}

export function getCompanySplitState(values: CompanySplitFormValues): {
  isCoFunded: boolean;
  isCompanyFundingEverything: boolean;
} {
  const isCoFunded =
    values.companySplitEnabled && values.payerCompanyId != null;
  return {
    isCoFunded,
    isCompanyFundingEverything:
      isCoFunded &&
      companyShareCoversWholePrice({
        shareType: values.companyShareType,
        shareValue: values.companyShareValue,
        price: values.price,
      }),
  };
}
